import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, EmptyState, Confirm, useBrand } from "../components/Shared";
import { TableSkeleton } from "../components/Skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { fmtFCFA, fmtDate, ROLES } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Printer, ArrowLeft, Pencil, FileSignature, Mail } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const blankItem = () => ({ activity: "", description: "", unit: "", quantity: "", unit_cost: "" });
const blankForm = () => ({ project: "", client: "", description: "", client_address: "", client_email: "", client_phone: "", status: "Draft", quote_date: today(), valid_until: "", vat_percent: 0, notes: "", items: [blankItem()] });

export default function Quotations() {
  const qc = useQueryClient();
  const brand = useBrand();
  const [mode, setMode] = useState("list"); // list | form | view
  const [form, setForm] = useState(blankForm());
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [convertTarget, setConvertTarget] = useState(null);
  const [convertAmount, setConvertAmount] = useState("");
  const [converting, setConverting] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const { data: rows = [], isLoading } = useQuery({ queryKey: ["quotations"], queryFn: () => api.get("/quotations").then((r) => r.data) });
  const { isRole } = useAuth();
  const [statusFilter, setStatusFilter] = useState("all");
  const shown = statusFilter === "all" ? rows : rows.filter((q) => (q.status || "Draft") === statusFilter);
  const STATUS_STYLES = { Draft: "bg-slate-100 text-slate-600", Sent: "bg-blue-50 text-blue-700", Accepted: "bg-emerald-50 text-emerald-700", Rejected: "bg-red-50 text-red-700" };

  const startNew = () => { setForm(blankForm()); setEditingId(null); setMode("form"); };
  const startEdit = (q) => {
    setForm({
      project: q.project || "", client: q.client || "", description: q.description || "",
      client_address: q.client_address || "", client_email: q.client_email || "", client_phone: q.client_phone || "", status: q.status || "Draft",
      quote_date: (q.quote_date || "").slice(0, 10) || today(), valid_until: (q.valid_until || "").slice(0, 10),
      vat_percent: q.vat_percent || 0, notes: q.notes || "",
      items: (q.items || []).map((it) => ({ activity: it.activity || "", description: it.description || "", unit: it.unit || "", quantity: it.quantity ?? "", unit_cost: it.unit_cost ?? "" })),
    });
    if (!(q.items || []).length) setForm((f) => ({ ...f, items: [blankItem()] }));
    setEditingId(q.id); setMode("form");
  };
  const openView = async (id) => {
    try { const { data } = await api.get(`/quotations/${id}`); setViewing(data); setMode("view"); }
    catch { toast.error("Could not open quotation."); }
  };

  const setItem = (idx, key, val) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items }));

  const num = (v) => (parseFloat(v) || 0);
  const subtotal = form.items.reduce((s, it) => s + num(it.quantity) * num(it.unit_cost), 0);
  const vatAmount = subtotal * num(form.vat_percent) / 100;
  const grand = subtotal + vatAmount;

  const save = async () => {
    if (!form.project.trim() || !form.client.trim()) { toast.error("Project and Client are required."); return; }
    if (!form.items.some((it) => it.activity.trim())) { toast.error("Add at least one activity."); return; }
    setSaving(true);
    const payload = {
      ...form, vat_percent: num(form.vat_percent),
      items: form.items.filter((it) => it.activity.trim() || num(it.quantity) || num(it.unit_cost))
        .map((it) => ({ ...it, quantity: num(it.quantity), unit_cost: num(it.unit_cost) })),
    };
    try {
      if (editingId) await api.put(`/quotations/${editingId}`, payload);
      else await api.post("/quotations", payload);
      qc.invalidateQueries({ queryKey: ["quotations"] });
      toast.success(editingId ? "Quotation updated." : "Quotation created.");
      setMode("list");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save quotation."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/quotations/${id}`); qc.invalidateQueries({ queryKey: ["quotations"] }); toast.success("Quotation deleted."); }
    catch { toast.error("Could not delete."); }
  };

  const openConvert = (q) => { setConvertTarget(q); setConvertAmount(String(q.grand_total ?? "")); };
  const doConvert = async () => {
    if (!convertTarget) return;
    const amt = parseFloat(convertAmount);
    if (!(amt > 0)) { toast.error("Enter a valid final amount."); return; }
    setConverting(true);
    try {
      await api.post(`/quotations/${convertTarget.id}/convert`, { final_amount: amt });
      qc.invalidateQueries({ queryKey: ["quotations"] }); qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project created from this quotation.");
      setConvertTarget(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not convert."); }
    finally { setConverting(false); }
  };
  const setQuoteStatus = async (id, status) => {
    try { await api.patch(`/quotations/${id}/status`, { status }); qc.invalidateQueries({ queryKey: ["quotations"] }); }
    catch { toast.error("Could not update status."); }
  };
  const openEmail = (q) => { setEmailTo(q.client_email || ""); setEmailTarget(q); };
  const sendEmail = async () => {
    if (!emailTarget) return;
    const to = emailTo.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) { toast.error("Enter a valid email address."); return; }
    setSendingEmail(true);
    try {
      await api.post(`/quotations/${emailTarget.id}/email`, { to });
      toast.success(`Quotation emailed to ${to}.`);
      setEmailTarget(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send email."); }
    finally { setSendingEmail(false); }
  };

  // ---------------- VIEW / PRINT ----------------
  if (mode === "view" && viewing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between no-print">
          <Button variant="outline" onClick={() => setMode("list")} data-testid="quote-back"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
          <Button onClick={() => window.print()} data-testid="quote-print-btn" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Printer className="h-4 w-4 mr-2" /> Print</Button>
        </div>
        <QuoteDocument q={viewing} brand={brand} />
      </div>
    );
  }

  // ---------------- FORM ----------------
  if (mode === "form") {
    return (
      <div className="space-y-6" data-testid="quote-form">
        <div className="flex items-center justify-between">
          <PageHeader title={editingId ? "Edit Quotation" : "New Quotation"} subtitle="Fill the details, add activities, then save to print." />
          <Button variant="outline" onClick={() => setMode("list")}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Project *</Label><Input data-testid="quote-project" value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} placeholder="Project name" /></div>
            <div className="space-y-1.5"><Label>Client *</Label><Input data-testid="quote-client" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} placeholder="Client name" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Input data-testid="quote-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description" /></div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Client address</Label><Input data-testid="quote-client-address" value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} placeholder="Street, City" /></div>
            <div className="space-y-1.5"><Label>Client email</Label><Input data-testid="quote-client-email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="client@email.com" /></div>
            <div className="space-y-1.5"><Label>Client phone</Label><Input data-testid="quote-client-phone" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} placeholder="+237677123456" /></div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Quotation date</Label><Input type="date" data-testid="quote-date" value={form.quote_date} onChange={(e) => setForm({ ...form, quote_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Valid until</Label><Input type="date" data-testid="quote-valid-until" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>VAT %</Label><Input type="number" min="0" step="0.01" data-testid="quote-vat" value={form.vat_percent} onChange={(e) => setForm({ ...form, vat_percent: e.target.value })} /></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left w-12">S/N</th>
                  <th className="px-3 py-2 text-left">Activity</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-left w-24">Unit</th>
                  <th className="px-3 py-2 text-right w-24">Qty</th>
                  <th className="px-3 py-2 text-right w-32">Unit cost</th>
                  <th className="px-3 py-2 text-right w-36">Total</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => (
                  <tr key={idx} className="border-t border-slate-100" data-testid={`quote-item-${idx}`}>
                    <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                    <td className="px-2 py-1.5"><Input data-testid={`quote-item-activity-${idx}`} value={it.activity} onChange={(e) => setItem(idx, "activity", e.target.value)} placeholder="Activity" /></td>
                    <td className="px-2 py-1.5"><Input value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="Details" /></td>
                    <td className="px-2 py-1.5"><Input value={it.unit} onChange={(e) => setItem(idx, "unit", e.target.value)} placeholder="pcs / m / hr" /></td>
                    <td className="px-2 py-1.5"><Input type="number" min="0" step="any" className="text-right" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} /></td>
                    <td className="px-2 py-1.5"><Input type="number" min="0" step="any" className="text-right" value={it.unit_cost} onChange={(e) => setItem(idx, "unit_cost", e.target.value)} /></td>
                    <td className="px-3 py-2 text-right font-mono-num whitespace-nowrap">{fmtFCFA(num(it.quantity) * num(it.unit_cost))}</td>
                    <td className="px-2 py-2 text-center">
                      <button onClick={() => removeItem(idx)} data-testid={`quote-item-remove-${idx}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t border-slate-100">
            <Button variant="outline" onClick={addItem} data-testid="quote-add-item"><Plus className="h-4 w-4 mr-2" /> Add activity</Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <Label>Notes / Terms</Label>
            <Textarea data-testid="quote-notes" rows={5} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Payment terms, validity, conditions…" />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono-num" data-testid="quote-subtotal">{fmtFCFA(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">VAT ({num(form.vat_percent)}%)</span><span className="font-mono-num">{fmtFCFA(vatAmount)}</span></div>
            <div className="flex justify-between text-base font-semibold border-t border-slate-200 pt-2"><span>Grand Total</span><span className="font-mono-num text-[#1E3A8A]" data-testid="quote-grand-total">{fmtFCFA(grand)}</span></div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setMode("list")}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="quote-save" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">{saving ? "Saving…" : (editingId ? "Update Quotation" : "Save Quotation")}</Button>
        </div>
      </div>
    );
  }

  // ---------------- LIST ----------------
  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex items-center justify-between">
        <PageHeader title="Quotations" subtitle="Create, reprint and manage professional client quotations." />
        <Button onClick={startNew} data-testid="new-quotation-btn" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" /> New Quotation</Button>
      </div>

      {rows.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {["all", "Draft", "Sent", "Accepted", "Rejected"].map((s) => (
            <button key={s} data-testid={`quote-filter-${s}`} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${statusFilter === s ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? <TableSkeleton cols={7} /> : rows.length === 0 ? (
          <EmptyState text="No quotations yet." action={<Button onClick={startNew} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" /> New Quotation</Button>} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead><TableHead>Project</TableHead><TableHead>Client</TableHead>
                <TableHead>Date</TableHead><TableHead className="text-right">Grand Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((q) => (
                <TableRow key={q.id} data-testid="quotation-row">
                  <TableCell className="font-semibold">{q.number}</TableCell>
                  <TableCell>{q.project}</TableCell>
                  <TableCell>{q.client}</TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(q.quote_date)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtFCFA(q.grand_total)}</TableCell>
                  <TableCell>
                    <select value={q.status || "Draft"} onChange={(e) => setQuoteStatus(q.id, e.target.value)} data-testid={`quote-status-${q.id}`}
                      className={`text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer ${STATUS_STYLES[q.status] || STATUS_STYLES.Draft}`}>
                      {["Draft", "Sent", "Accepted", "Rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="stems-actions">
                      {isRole(ROLES.CEO, ROLES.ACEO) && q.status === "Accepted" && !q.project_id && (
                        <Button size="sm" onClick={() => openConvert(q)} data-testid={`quote-convert-${q.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">Convert to Project</Button>
                      )}
                      {q.project_id && <span className="text-xs text-emerald-600 font-medium mr-1">Converted</span>}
                      <Button size="sm" variant="outline" onClick={() => openEmail(q)} data-testid={`quote-email-${q.id}`} title="Email to client"><Mail className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => openView(q.id)} data-testid={`quote-view-${q.id}`}><Printer className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => startEdit(q)} data-testid={`quote-edit-${q.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Confirm onConfirm={() => remove(q.id)} title="Delete quotation?" description={`This permanently removes ${q.number}.`}
                        trigger={<Button size="sm" variant="outline" className="text-red-600" data-testid={`quote-delete-${q.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!convertTarget} onOpenChange={(o) => { if (!o) setConvertTarget(null); }}>
        <DialogContent data-testid="convert-dialog">
          <DialogHeader><DialogTitle>Convert to Project</DialogTitle>
            <DialogDescription>Set the final agreed amount for the new project.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Quotation grand total: <span className="font-semibold font-mono-num">{fmtFCFA(convertTarget?.grand_total)}</span>.
              Adjust the final agreed amount if it was negotiated with the client.
            </p>
            <div>
              <Label htmlFor="final-amount">Final accepted amount (FCFA)</Label>
              <Input id="final-amount" type="number" min="0" value={convertAmount}
                onChange={(e) => setConvertAmount(e.target.value)} data-testid="convert-final-amount" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertTarget(null)} data-testid="convert-cancel-btn">Cancel</Button>
            <Button onClick={doConvert} disabled={converting} data-testid="convert-confirm-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {converting ? "Converting…" : "Create Project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailTarget} onOpenChange={(o) => { if (!o) setEmailTarget(null); }}>
        <DialogContent data-testid="email-dialog">
          <DialogHeader><DialogTitle>Email quotation to client</DialogTitle>
            <DialogDescription>Send a secure link to view and download the quotation PDF.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Sends <span className="font-semibold">{emailTarget?.number}</span> to the client with a secure link to view and download the PDF.
            </p>
            <div>
              <Label htmlFor="email-to">Client email</Label>
              <Input id="email-to" type="email" value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)} placeholder="client@email.com" data-testid="email-to-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailTarget(null)} data-testid="email-cancel-btn">Cancel</Button>
            <Button onClick={sendEmail} disabled={sendingEmail} data-testid="email-send-btn" className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white">
              {sendingEmail ? "Sending…" : "Send email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuoteDocument({ q, brand }) {
  return (
    <div className="quote-print bg-white rounded-xl border border-slate-200 shadow-sm p-8 max-w-4xl mx-auto text-slate-900" data-testid="quote-document">
      {/* Header: logo + company + title */}
      <div className="flex items-start justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          {brand.logo_url ? <img src={brand.logo_url} alt="Logo" className="qt-logo h-14 w-14 rounded-lg object-cover" /> : <div className="qt-logo h-14 w-14 rounded-lg bg-[#0B132B] flex items-center justify-center text-white"><FileSignature className="h-7 w-7" /></div>}
          <div>
            <div className="font-heading text-xl font-bold">{brand.name}</div>
            <div className="text-xs text-slate-500">Enterprise Management System</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading text-2xl font-bold tracking-wide">QUOTATION</div>
          <div className="text-sm mt-1"><span className="text-slate-500">No: </span><span className="font-semibold">{q.number}</span></div>
          <div className="text-sm"><span className="text-slate-500">Date: </span>{fmtDate(q.quote_date)}</div>
          {q.valid_until ? <div className="text-sm"><span className="text-slate-500">Valid until: </span>{fmtDate(q.valid_until)}</div> : null}
        </div>
      </div>

      {/* Project / Client / Description */}
      <div className="grid grid-cols-3 gap-4 mt-5 text-sm">
        <div><div className="text-xs uppercase text-slate-400">Project</div><div className="font-medium" data-testid="doc-project">{q.project || "—"}</div></div>
        <div><div className="text-xs uppercase text-slate-400">Client</div><div className="font-medium" data-testid="doc-client">{q.client || "—"}</div>
          {q.client_address ? <div className="text-xs text-slate-600 whitespace-pre-wrap">{q.client_address}</div> : null}
          {q.client_email ? <div className="text-xs text-slate-600">{q.client_email}</div> : null}
          {q.client_phone ? <div className="text-xs text-slate-600">{q.client_phone}</div> : null}
        </div>
        <div><div className="text-xs uppercase text-slate-400">Description</div><div className="font-medium">{q.description || "—"}</div></div>
      </div>

      {/* Items */}
      <table className="w-full text-sm mt-6 border border-slate-200">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
          <tr>
            <th className="px-3 py-2 text-left border-b border-slate-200">S/N</th>
            <th className="px-3 py-2 text-left border-b border-slate-200">Activity</th>
            <th className="px-3 py-2 text-left border-b border-slate-200">Description</th>
            <th className="px-3 py-2 text-left border-b border-slate-200">Unit</th>
            <th className="px-3 py-2 text-right border-b border-slate-200">Qty</th>
            <th className="px-3 py-2 text-right border-b border-slate-200">Unit cost</th>
            <th className="px-3 py-2 text-right border-b border-slate-200">Total</th>
          </tr>
        </thead>
        <tbody>
          {(q.items || []).map((it) => (
            <tr key={it.sn} className="border-b border-slate-100">
              <td className="px-3 py-2">{it.sn}</td>
              <td className="px-3 py-2 font-medium">{it.activity}</td>
              <td className="px-3 py-2 text-slate-600">{it.description}</td>
              <td className="px-3 py-2">{it.unit}</td>
              <td className="px-3 py-2 text-right font-mono-num">{it.quantity}</td>
              <td className="px-3 py-2 text-right font-mono-num">{fmtFCFA(it.unit_cost)}</td>
              <td className="px-3 py-2 text-right font-mono-num">{fmtFCFA(it.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end mt-4">
        <div className="w-72 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono-num">{fmtFCFA(q.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">VAT ({q.vat_percent || 0}%)</span><span className="font-mono-num">{fmtFCFA(q.vat_amount)}</span></div>
          <div className="flex justify-between text-base font-bold border-t border-slate-300 pt-1"><span>Grand Total</span><span className="font-mono-num">{fmtFCFA(q.grand_total)}</span></div>
          {q.final_amount != null ? (
            <div className="flex justify-between text-sm font-semibold text-emerald-700 border-t border-slate-200 pt-1" data-testid="doc-final-amount"><span>Final agreed price</span><span className="font-mono-num">{fmtFCFA(q.final_amount)}</span></div>
          ) : null}
        </div>
      </div>

      {q.notes ? (
        <div className="mt-6 text-sm">
          <div className="text-xs uppercase text-slate-400 mb-1">Notes / Terms</div>
          <div className="whitespace-pre-wrap text-slate-700">{q.notes}</div>
        </div>
      ) : null}

      {/* Signature */}
      <div className="mt-12 flex justify-between items-end text-sm">
        <div className="text-xs text-slate-400">Generated on {fmtDate(q.created_at)}</div>
        <div className="text-center">
          <div className="h-12 border-b border-slate-400 w-56 mb-1"></div>
          <div className="font-semibold">Chief Executive Officer</div>
          <div className="text-xs text-slate-500">{brand.name}</div>
        </div>
      </div>
    </div>
  );
}
